import React, { useState, useEffect } from 'react';
import { 
  X, Mail, Send, Loader2, ChevronDown, FileText, 
  History, CheckCircle, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailCandidateModal = ({ 
  isOpen, 
  onClose, 
  candidateEmail, 
  candidateName,
  candidateId,
  challengeId 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      if (candidateEmail) {
        fetchEmailHistory();
      }
    }
  }, [isOpen, candidateEmail]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/company/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const res = await axios.get(
        `${API}/api/company/email-history?candidate_email=${encodeURIComponent(candidateEmail)}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setEmailHistory(res.data.emails || []);
    } catch (err) {
      console.error('Failed to fetch email history:', err);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.subject);
      setBody(template.body);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in subject and body');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/company/send-email`,
        {
          to_email: candidateEmail,
          subject,
          body,
          template_type: selectedTemplate || 'custom',
          candidate_id: candidateId,
          challenge_id: challengeId
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(res.data.message || 'Email sent!');
      onClose();
      
      // Reset form
      setSubject('');
      setBody('');
      setSelectedTemplate('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Mail className="text-purple-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Email Candidate</h3>
              <p className="text-sm text-gray-500">
                {candidateName || candidateEmail}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {emailHistory.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`p-2 rounded-lg transition-colors ${
                  showHistory ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100 text-gray-500'
                }`}
                title="View email history"
              >
                <History size={20} />
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {showHistory ? (
            // Email History View
            <div>
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <History size={16} />
                Email History ({emailHistory.length})
              </h4>
              
              {emailHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No emails sent yet</p>
              ) : (
                <div className="space-y-3">
                  {emailHistory.map((email) => (
                    <div 
                      key={email.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-gray-900 text-sm">{email.subject}</p>
                        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          email.status === 'sent' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {email.status === 'sent' ? <CheckCircle size={10} /> : <Clock size={10} />}
                          {email.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{email.body}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(email.sent_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => setShowHistory(false)}
                className="w-full mt-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm"
              >
                Back to Compose
              </button>
            </div>
          ) : (
            // Compose View
            <div className="space-y-4">
              {/* To Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="email"
                  value={candidateEmail}
                  disabled
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700"
                />
              </div>

              {/* Template Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template (optional)
                </label>
                <div className="relative">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Choose a template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject..."
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your message..."
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use {`{{candidate_name}}`} and {`{{company_name}}`} for personalization
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showHistory && (
          <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={loading || !subject.trim() || !body.trim()}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Email
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCandidateModal;
